import { useMemo, useState } from 'react';
import { Goal } from '@/types/goal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HabitCheckbox } from '@/components/HabitCheckbox';
import { HabitHistoryHeatmap } from './HabitHistoryHeatmap';
import { TabStatsLine } from './TabStatsLine';
import { StreakMilestoneModal } from '@/components/StreakMilestoneModal';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { CheckCircle2, Flame, Calendar, TrendingUp, Lock, Sparkles, ListChecks } from 'lucide-react';
import { format, subDays, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { useDateFormat } from '@/contexts/DateFormatContext';
import { useProfile } from '@/hooks/useProfile';
import { getTierFromProfile, TIER_LIMITS } from '@/lib/tier-utils';
import { isHabitDueToday, getScheduledDaysInWeek } from '@/lib/habit-schedule';
import { BETA_CONFIG } from '@/config/beta';
import { ValuesGateBanner } from '@/components/journey/ValuesGateBanner';

export interface HabitToggleResult {
  milestoneReached?: number;
  habitTitle?: string;
  newStreak?: number;
}

interface HabitTrackingTabProps {
  goals: Goal[];
  valuesComplete?: boolean;
  hasPlan?: boolean;
  onNavigateToValues?: () => void;
  onToggleHabit: (goalId: string, habitId: string) => Promise<HabitToggleResult | void>;
  totalStreak: number;
  todayProgress: number;
  onNavigateToPlan?: (milestoneIndex: number) => void;
}

export const HabitTrackingTab = ({
  goals,
  valuesComplete,
  hasPlan,
  onNavigateToValues,
  onToggleHabit,
  totalStreak,
  todayProgress,
  onNavigateToPlan,
}: HabitTrackingTabProps) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const { profile } = useProfile();
  const { formatDate: fmtDate } = useDateFormat();
  const [milestoneModal, setMilestoneModal] = useState<{
    isOpen: boolean;
    milestone: number;
    habitTitle: string;
  }>({ isOpen: false, milestone: 0, habitTitle: '' });

  // Tier from cached profile (synchronous)
  const userTier = getTierFromProfile(profile);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [showAllHabits, setShowAllHabits] = useState(false);

  const now = new Date();
  const allHabits = goals.flatMap(goal =>
    goal.habits
      .filter(habit => isHabitDueToday(habit.frequency, habit.scheduleDays, habit.createdAt, now))
      .map(habit => {
        let isCompletedToday = habit.completedDates.includes(today);
        // Weekly habits: mark as complete if completed any day this week
        if (habit.frequency === 'weekly' && !isCompletedToday) {
          const dayOfWeek = now.getDay();
          const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() + mondayOffset);
          weekStart.setHours(0, 0, 0, 0);
          isCompletedToday = habit.completedDates.some(d => {
            const date = new Date(d + 'T00:00:00');
            return date >= weekStart && date <= now;
          });
        }
        return {
          ...habit,
          goalId: goal.id,
          goalTitle: goal.title,
          isCompletedToday,
        };
      })
  );

  const handleToggleHabit = async (goalId: string, habitId: string) => {
    const result = await onToggleHabit(goalId, habitId);
    if (result && 'milestoneReached' in result && result.milestoneReached) {
      setMilestoneModal({
        isOpen: true,
        milestone: result.milestoneReached,
        habitTitle: result.habitTitle || '',
      });
    }
  };

  const completedToday = allHabits.filter(h => h.isCompletedToday).length;
  const totalHabits = allHabits.length;

  // Calculate weekly stats using scheduled days per habit
  const weeklyStats = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });

    let completedThisWeek = 0;
    let totalPossibleThisWeek = 0;

    // Use all habits (not just due-today filtered ones) for weekly stats
    const allHabitsForStats = goals.flatMap(goal => goal.habits);

    allHabitsForStats.forEach(habit => {
      const scheduledDays = getScheduledDaysInWeek(habit.frequency, habit.scheduleDays, habit.createdAt, new Date());
      totalPossibleThisWeek += scheduledDays;

      for (let i = 0; i < 7; i++) {
        const dayDate = subDays(new Date(), i);
        if (isWithinInterval(dayDate, { start: weekStart, end: weekEnd })) {
          const day = format(dayDate, 'yyyy-MM-dd');
          if (isHabitDueToday(habit.frequency, habit.scheduleDays, habit.createdAt, dayDate) && habit.completedDates?.includes(day)) {
            completedThisWeek++;
          }
        }
      }
    });

    const completionRate = totalPossibleThisWeek > 0
      ? Math.round((completedThisWeek / totalPossibleThisWeek) * 100)
      : 0;

    return { completedThisWeek, totalPossibleThisWeek, completionRate };
  }, [goals]);

  if (totalHabits === 0) {
    // Pre-values users see a gate banner + preview
    if (!valuesComplete) {
      return (
        <div>
          <ValuesGateBanner onNavigateToValues={() => onNavigateToValues?.()} />
          <div className="text-center py-12 space-y-4 opacity-60">
            <div className="inline-flex p-5 rounded-full bg-gradient-to-br from-primary/10 to-accent/10">
              <Flame className="w-10 h-10 text-primary/60" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-display font-bold">Build Daily Habits That Stick</h2>
              <p className="text-muted-foreground max-w-md mx-auto text-sm">
                Track daily habits linked to your goals with streak tracking, history heatmaps, and milestone celebrations.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <Card className="card-elevated">
        <CardContent className="py-16 text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-primary/50" />
          <h3 className="text-xl font-semibold mb-2">No Habits Yet</h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            {hasPlan
              ? 'Your development plan has suggested habits ready for you to review.'
              : 'Add habits to your goals to start tracking your daily progress.'}
          </p>
          {hasPlan && onNavigateToPlan && (
            <Button onClick={() => onNavigateToPlan(0)} className="gap-2">
              <ListChecks className="w-4 h-4" />
              View Suggested Habits
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Calculate limit info for free users
  const habitLimit = TIER_LIMITS[userTier].max_habits;
  const isAtLimit = !BETA_CONFIG.allFeaturesUnlocked && habitLimit !== -1 && totalHabits >= habitLimit;
  const showLimitIndicator = !BETA_CONFIG.allFeaturesUnlocked && userTier === 'free' && habitLimit !== -1;

  return (
    <div className="space-y-6">
      {/* Tab Header Stats */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            Habit Tracking
          </h2>
          {showLimitIndicator && (
            <span className={`text-sm font-medium ${isAtLimit ? 'text-orange-500' : 'text-muted-foreground'}`}>
              {totalHabits}/{habitLimit} used {isAtLimit && <Lock className="inline w-3.5 h-3.5 ml-0.5" />}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Build consistency through daily habits
        </p>
        {isAtLimit && (
          <Button
            variant="link"
            className="p-0 h-auto text-primary hover:text-primary/80 mt-1"
            onClick={() => setUpgradeModalOpen(true)}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            Upgrade for unlimited habits →
          </Button>
        )}
        <TabStatsLine 
          stats={[
            { value: `${totalStreak}-day`, label: 'streak', icon: <Flame className="w-3.5 h-3.5 text-orange-500" /> },
            { value: `${weeklyStats.completedThisWeek}/${weeklyStats.totalPossibleThisWeek}`, label: 'this week' },
            { value: `${weeklyStats.completionRate}%`, label: 'completion rate', icon: <TrendingUp className="w-3.5 h-3.5" /> },
          ]}
        />
      </div>

      {/* Progress Bar */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Today's Habits
          </CardTitle>
          <CardDescription>
            {fmtDate(new Date(), 'weekday')} — {completedToday}/{totalHabits} completed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Progress value={todayProgress} className="h-3 mb-6" />
          
          <div className="space-y-6">
            {(() => {
              let habitCount = 0;
              return goals.map(goal => {
                const goalHabits = allHabits.filter(h => h.goalId === goal.id);
                if (goalHabits.length === 0) return null;
                if (!showAllHabits && habitCount >= 5) return null;

                const visibleHabits = showAllHabits ? goalHabits : goalHabits.slice(0, 5 - habitCount);
                habitCount += visibleHabits.length;

                return (
                  <div key={goal.id} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm">{goal.title}</h4>
                      <Badge variant="outline" className="text-xs">
                        {goalHabits.filter(h => h.isCompletedToday).length}/{goalHabits.length}
                      </Badge>
                    </div>
                    <div className="space-y-2 pl-2">
                      {visibleHabits.map(habit => (
                        <HabitCheckbox
                          key={habit.id}
                          title={habit.title}
                          completed={habit.isCompletedToday}
                          streak={habit.streak}
                          onToggle={() => handleToggleHabit(habit.goalId, habit.id)}
                          linkedAction={habit.linkedAction}
                          onNavigateToPlan={onNavigateToPlan}
                        />
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          {totalHabits > 5 && (
            <div className="flex justify-center pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllHabits(!showAllHabits)}
                className="text-muted-foreground hover:text-foreground"
              >
                {showAllHabits ? 'Show less' : `Show all ${totalHabits} habits`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Habit History Heatmap */}
      <HabitHistoryHeatmap goals={goals} />

      {/* Streak Milestone Modal */}
      <StreakMilestoneModal
        isOpen={milestoneModal.isOpen}
        onClose={() => setMilestoneModal({ isOpen: false, milestone: 0, habitTitle: '' })}
        milestone={milestoneModal.milestone}
        habitTitle={milestoneModal.habitTitle}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        trigger="habit_limit"
      />
    </div>
  );
};
